import type { Logger } from '@/types/Logger';
import type { MissingFileKeyHelpRequest, FileNeedingNewKey } from '@/types/fileKey/FileKeyFacilitation';
import type { HeliaNode } from '@/types/poc/helia';
import type { PocServices } from '@/types/services.types';
import type { AppDispatch } from '@/store';
import {
  facilitationComplete,
  fileKeyFailed,
  fileKeyProcessed,
  startFacilitation,
} from '@/store/slices/fileKey/fileKeyFacilitationSlice';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { ensureDecapsPrivateKey } from '@/store/thunks/auth/ensureDecapsPrivateKey';
import { FileEncryptionService } from '@/services/abstract/poc/fileEncryption.service.interface';
import { parseJsonArrayToBytes } from '@/utils/hex.utils';

const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;

interface FacilitationContext {
  facilitatorPrivateKey: Uint8Array;
  currentUserId: string;
  dispatch: AppDispatch;
  pocServices: PocServices;
  fileKeyFacilitationService: {
    submitNewFileKey: (sixId: string, fileId: string, newFileKeyCreatedForUserId: string, newFileKeyCID: string) => Promise<void>;
  };
  log: Logger;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMissingFileKeyHelpRequestArray(value: unknown): value is MissingFileKeyHelpRequest[] {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.every((entry) => {
    if (!isRecord(entry)) {
      return false;
    }

    const requestor = entry.requestor;
    return (
      isRecord(requestor) &&
      typeof requestor.userId === 'string' &&
      typeof requestor.encapsKey === 'string' &&
      Array.isArray(entry.filesNeedingNewFileKey)
    );
  });
}

export const facilitateFileKeys = createAppAsyncThunk(
  'fileKeyFacilitation/facilitate',
  async(_, { dispatch, getState, extra, rejectWithValue }) => {
    const {
      pocServices,
      userService,
      fileKeyFacilitationService,
      loggerService,
    } = extra;
    const log = loggerService.createLogger({ namespace: 'six3ro:file-key-facilitation' });

    try {
      log.info('Checking for missing file key requests...');
      const rawRequests = await fileKeyFacilitationService.getMissingFileKeyRequests();
      log.debug('Missing file key response received', rawRequests);

      if (!isMissingFileKeyHelpRequestArray(rawRequests)) {
        log.error('Missing file key response has unexpected shape', rawRequests);
        throw new Error('Invalid missing file key response shape');
      }

      const requests = rawRequests;
      if (requests.length === 0) {
        log.info('No missing file key requests. Nothing to facilitate.');
        return { processed: 0, total: 0 };
      }

      const totalFiles = requests.reduce((sum, req) => sum + req.filesNeedingNewFileKey.length, 0);
      log.info(`Found ${requests.length} requestor(s), ${totalFiles} file(s) to facilitate.`);
      dispatch(startFacilitation({ totalCount: totalFiles }));

      const state = getState();
      const currentUserId = state.user.user?.id;
      if (currentUserId == null) {
        throw new Error('User not authenticated');
      }

      const facilitatorPrivateKey = await ensureDecapsPrivateKey({
        decapsKeyHex: state.auth.decapsKey,
        ukekHex: state.auth.ukek,
        userId: currentUserId,
        dispatch,
        userService,
        cryptoService: pocServices.cryptoService,
        log,
      });

      const ctx: FacilitationContext = {
        facilitatorPrivateKey,
        currentUserId,
        dispatch,
        pocServices,
        fileKeyFacilitationService,
        log,
      };

      for (const request of requests) {
        await processRequestor(request, ctx);
      }

      dispatch(facilitationComplete());
      const finalState = getState();
      const result = {
        processed: finalState.fileKeyFacilitation.processedCount,
        total: totalFiles,
        errors: finalState.fileKeyFacilitation.errors,
      };
      log.info('File key facilitation complete', result);
      return result;
    } catch (error) {
      // Slice already holds the real processed/total/error counts via the
      // dispatches above; facilitationComplete() flips status to 'error' when
      // any file failed. Reject honestly instead of reporting a fake success —
      // every caller fires this fire-and-forget (never unwraps), so nothing
      // surfaces to the UI.
      log.error('File key facilitation failed', error);
      dispatch(facilitationComplete());
      return rejectWithValue({
        message: error instanceof Error ? error.message : 'File key facilitation failed',
      });
    }
  }
);

async function processRequestor(
  request: MissingFileKeyHelpRequest,
  ctx: FacilitationContext
): Promise<void> {
  ctx.log.debug(
    `Processing ${request.filesNeedingNewFileKey.length} file(s) for requestor: ${request.requestor.userId}`
  );
  const recipientPublicKey = parseJsonArrayToBytes(request.requestor.encapsKey);

  for (const file of request.filesNeedingNewFileKey) {
    try {
      await processOneFileKey(file, recipientPublicKey, request.requestor.userId, ctx);
      ctx.dispatch(fileKeyProcessed());
    } catch (error) {
      const message = `Failed for file ${file.fileId} (six: ${file.sixId}): ${error instanceof Error ? error.message : 'Unknown error'}`;
      ctx.log.error(message);
      ctx.dispatch(fileKeyFailed(message));
    }
  }
}

async function processOneFileKey(
  file: FileNeedingNewKey,
  recipientPublicKey: Uint8Array,
  requestorUserId: string,
  ctx: FacilitationContext
): Promise<void> {
  const { facilitatorPrivateKey, currentUserId, pocServices, fileKeyFacilitationService, log } = ctx;
  const { p2pStackManagerService, fileEncryptionService, fileStoreService } = pocServices;

  const helia: HeliaNode | null = p2pStackManagerService.getHeliaNode();
  if (helia === null) {
    throw new Error(`Helia node not initialized for six: ${file.sixId}`);
  }

  log.debug(`Fetching facilitator file key CID: ${file.facilitatorFileKeyCID}`);
  const encryptionJsonBytes = await fileStoreService.getFile(helia, file.facilitatorFileKeyCID, {
    signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS),
  });
  const facilitatorEncryption = FileEncryptionService.parseRecipientKeyBlob(
    new TextDecoder().decode(new Uint8Array(encryptionJsonBytes)),
    currentUserId
  );

  const reEncrypted = await fileEncryptionService.reEncryptFileKeyForRecipient(
    facilitatorEncryption,
    facilitatorPrivateKey,
    recipientPublicKey
  );
  log.debug(`Re-encrypted file key for requestor: ${requestorUserId}, file: ${file.fileId}`);

  const blobBytes = new TextEncoder().encode(
    FileEncryptionService.serializeRecipientKeyBlob(reEncrypted)
  );
  const blobFile = new File([blobBytes], `filekey-${file.fileId}-${requestorUserId}.json`, {
    type: 'application/octet-stream',
  });

  const addResult = await fileStoreService.addFile(helia, blobFile);
  const newFileKeyCID = addResult.cid.toString();
  log.debug(`Stored new file key in Helia, CID: ${newFileKeyCID}, requestor: ${requestorUserId}`);

  await fileKeyFacilitationService.submitNewFileKey(
    file.sixId,
    file.fileId,
    requestorUserId,
    newFileKeyCID,
  );
  log.debug(`Submitted new file key CID to API for requestor: ${requestorUserId}, file: ${file.fileId}`);
}
